import * as ko from 'knockout'
import { isRacePageDto } from '@mobi/api-types'
import { getRacePageDataFromApi } from '@core/Areas/Racing/api'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import {
  getFobPriceChangeTopic,
  getToteRaceTopic,
  iotSubscribeTopics as subscribeRaceEvent,
  iotUnsubscribeTopics as unsubscribeRaceEvent,
} from '@core/State/PushData'
import type { IDisposable } from '../../../../AppUtils/Framework/Disposable/IDisposable'
import Guard from '../../../../AppUtils/Framework/Guard'
import type { IUseGiddyUp } from '../../../Components/Pages/Starters/IUseGiddyUp'
import StartersMapper from '../../../Mapping/StartersMapper'
import { BettingInformation } from '../../../Model/BettingInformation'
import type { IObservableStarter } from '../../../Model/Observables/IObservableStarter'
import ObservableRaceKey from '../../../Model/Observables/ObservableRaceKey'
import ObservableRacePage from '../../../Model/Observables/ObservableRacePage'
import type { IProvDiv } from '../BaseBetType/IBaseBetType'

export interface MultiLegViewModelParams {
  data: ObservableRacePage
  selections: ko.ObservableArray<IObservableStarter>
  context: { bettingContext: BettingInformation }
  useGiddyUp: IUseGiddyUp
}

export class MultiLegViewModel implements IDisposable, IProvDiv {
  private legVisibleSubscriptions: ko.Subscription
  private subscribedTopics: string[] = []

  public data: ObservableRacePage
  public bettingContext: BettingInformation
  public selections: ko.ObservableArray<IObservableStarter>
  public legVisible: ko.Observable<number | null>
  public doubleProvDivAllowed: ko.PureComputed<boolean>
  public quaddieProvDivAllowed: ko.PureComputed<boolean>
  public useGiddyUp?: IUseGiddyUp

  constructor(params: MultiLegViewModelParams) {
    Guard.notNull(params.data)
    Guard.notNull(params.context.bettingContext)

    this.data = params.data
    this.useGiddyUp = params.useGiddyUp

    this.bettingContext = params.context.bettingContext
    this.selections = params.selections

    this.legVisible = ko.observable<number>(null)
    this.bettingContext.legVisible = this.legVisible

    this.doubleProvDivAllowed = ko.pureComputed(() => {
      return (
        this.bettingContext.selectedBetType().betType() === 'WinPlace' &&
        this.data.hasDoubleProvDivs()
      )
    })

    this.quaddieProvDivAllowed = ko.pureComputed(() => {
      return (
        this.bettingContext.selectedBetType().betType() === 'WinPlace' &&
        this.data.hasQuaddieProvDivs()
      )
    })

    // Register Handlers
    this.legVisibleSubscriptions = this.legVisible.subscribe(() => {
      this.loadStartersForLeg()
    })
  }

  public isValidMultiLeg(raceKey: ObservableRaceKey): boolean {
    if (this.bettingContext.selectedBetType().betType() === 'Double') {
      return raceKey.isDoubleLeg()
    } else if (this.bettingContext.selectedBetType().betType() === 'Quaddie') {
      return raceKey.isQuaddieLeg()
    }
    return false
  }

  public dispose() {
    this.unsubscribeFromPushData()
    this.legVisibleSubscriptions.dispose()
  }

  private loadStartersForLeg(): void {
    //Only load starters for leg if not loaded previously
    const legVisible = this.legVisible()
    if (legVisible && legVisible >= 0) {
      const startersForLeg = this.data.getStartersForLeg(legVisible)
      if (startersForLeg().length <= 0) {
        const raceKeyForLeg = this.data.getRaceKeyForLeg(legVisible)

        if (raceKeyForLeg) {
          this.loadStartersForRace(raceKeyForLeg)
        }
      } else {
        this.subscribeToPushData()
      }
    }
  }

  private subscribeToPushData() {
    try {
      const legVisible = this.legVisible()
      if (legVisible) {
        const newTopics: string[] = []
        const raceKeyForLeg = this.data.getRaceKeyForLeg(legVisible)

        if (!raceKeyForLeg) {
          return
        }

        const currLegRaceNumber = raceKeyForLeg.raceNumber()
        const currLegRace = this.data.meetingInformation
          .races()
          .filter(race => race.raceNumber() === currLegRaceNumber)[0]

        if (
          !!currLegRace &&
          currLegRace.fixedOddsInfo &&
          currLegRace.fixedOddsInfo.marketSequence &&
          currLegRace.fixedOddsInfo.marketSequence()
        ) {
          const marketId = currLegRace.fixedOddsInfo.marketSequence().toString()
          // FOB Price Change
          newTopics.push(getFobPriceChangeTopic(marketId))
        }
        if (!!currLegRace && currLegRace.key) {
          // TOTE Price Change
          newTopics.push(getToteRaceTopic(currLegRace.key(), { type: 'priceChanged' }))
        }
        subscribeRaceEvent(newTopics)
        this.subscribedTopics = this.subscribedTopics.concat(newTopics)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error Subscribing', e)
    }
  }

  private unsubscribeFromPushData() {
    unsubscribeRaceEvent(this.subscribedTopics)
    this.subscribedTopics.length = 0
  }

  private async loadStartersForRace(raceKey: ObservableRaceKey) {
    if (raceKey == null) return

    const raceNumber = raceKey.raceNumber()
    const { meetingId, meetingDate } = this.bettingContext

    try {
      const pageDto = await getRacePageDataFromApi(meetingId, meetingDate, raceNumber)

      queryClient.setQueryData(
        queryKeys.racePageComplete(meetingId, meetingDate, raceNumber),
        pageDto
      )

      if (isRacePageDto(pageDto)) {
        const raceStarters = pageDto.RaceStarters.find(s => s.RaceKey.RaceNumber === raceNumber)
        const starters = raceStarters?.Starters ?? []

        this.data.addStartersForRace(raceKey, StartersMapper.mapStarters(starters))

        this.subscribeToPushData()
      }
    } catch {
      this.data.addStartersForRace(raceKey, [])
    }
  }
}
